import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  SelectInput,
  Switch,
  ToggleInput,
  ToggleInputGroup,
} from '@ktag/styleguide/dist/react';
import { Button as ButtonAV } from '@agissc/ktag-map';
import useAppState from '../../AppStore';
import usePrintLayout from './api/usePrintLayout';
import PrintedList from './PrintedList';
import PrintVisibleLayers from './PrintVisibleLayers';
import Scrollable from '../../components/scrollable/Scrollable';
import PrintPlaceholder from './PrintPlaceholder';
import { useTracking } from '../../utils/tracking/TrackingContext';
import { scaleValues, getClosestScaleValue } from './api/printHelpers';
import usePrintSubmit from './api/usePrintSubmit';
import usePrintAngle from './usePrintAngle';

const Print = () => {
  const { t } = useTranslation('agisviewer');
  const { Tracking } = useTracking();

  const location = useAppState((state) => state.location);
  const layoutTitle = useAppState((state) => state.layoutTitle);
  const setLayoutTitle = useAppState((state) => state.setLayoutTitle);
  const layoutSubTitle = useAppState((state) => state.layoutSubTitle);
  const setLayoutSubTitle = useAppState((state) => state.setLayoutSubTitle);
  const printLegend = useAppState((state) => state.printLegend);
  const togglePrintLegend = useAppState((state) => state.togglePrintLegend);
  const printScale = useAppState((state) => state.printScale);
  const setPrintScale = useAppState((state) => state.setPrintScale);
  const layoutId = useAppState((state) => state.layoutId);
  const setLayoutId = useAppState((state) => state.setLayoutId);
  const formatValue = useAppState((state) => state.formatValue);
  const setFormatValue = useAppState((state) => state.setFormatValue);
  const orientation = useAppState((state) => state.orientation);
  const setOrientation = useAppState((state) => state.setOrientation);
  const printMapOnly = useAppState((state) => state.printMapOnly);
  const togglePrintMapOnly = useAppState((state) => state.togglePrintMapOnly);
  const resetPrint = useAppState((state) => state.resetPrint);
  const basemapId = useAppState((state) => state.selectedBasemapId);

  const [printButtonDisabled, setPrintButtonDisabled] = useState(true);
  const [printButtonTitle, setPrintButtonTitle] = useState('');

  const printLayers = usePrintLayout();
  const layouts = printLayers.data ?? [];

  const visibleLayersRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const format = layouts
    .find((item) => item.id === layoutId)
    ?.formats.find((item) => item.id === formatValue);

  const { onSubmit, topicLayers, selectedTopicLayers, layersConfigQueries } =
    usePrintSubmit({
      format,
      visibleLayersRefs,
      setPrintButtonDisabled,
    });

  const { inputAngle, handleAngleChanged, handleAngleFocus, handleAngleBlur } =
    usePrintAngle();

  useEffect(() => {
    const hasBasemap = !!topicLayers[basemapId];
    setPrintButtonDisabled(!(location?.center && location?.extent && hasBasemap));
    setPrintButtonTitle(hasBasemap ? '' : t('notification_basemap_invalid'));
  }, [location, topicLayers, basemapId]);

  useEffect(() => {
    const selectedLayout = layouts.find((item) => item.id === layoutId);
    if (selectedLayout) {
      setFormatValue(selectedLayout.formats[0].id);
      setOrientation(selectedLayout.formats[0].landscape ? 'landscape' : 'portrait');
    }
  }, [layoutId, layouts, setFormatValue]);

  useEffect(() => {
    const { resolution } = location;
    if (!resolution) return;
    setPrintScale(getClosestScaleValue(resolution));
  }, []);

  const handleResetPrint = () => {
    Tracking.Print.resetPrint();
    resetPrint();
    setPrintScale(
      location.resolution ? getClosestScaleValue(location.resolution) : scaleValues[1],
    );
  };

  if (printLayers.isLoading || layersConfigQueries.isLoading) return <PrintPlaceholder />;

  return (
    <div className="tooldialog__wrapper">
      <Scrollable additionalClassName="tooldialog__wrapper-content">
        <Input
          label={t('print_title')}
          name="Layouttitle"
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setLayoutTitle(e.target.value as string)
          }
          value={layoutTitle}
        />
        <Input
          label={t('print_subtitle')}
          name="Layoutsubtitle"
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setLayoutSubTitle(e.target.value as string)
          }
          value={layoutSubTitle}
        />
        {layouts.length > 1 && (
          <SelectInput
            label={t('print_layout')}
            name="LayoutId"
            onChange={(e: {
              target: {
                id: string;
                name: string;
                value: string | number | (string | number)[];
              };
            }) => setLayoutId(String(e.target.value))}
            options={layouts.map((item) => ({
              label: item.displayName,
              value: item.id,
            }))}
            value={layoutId}
          />
        )}
        <SelectInput
          label={t('print_format')}
          name="Layoutformat"
          onChange={(e: {
            target: {
              id: string;
              name: string;
              value: string | number | (string | number)[];
            };
          }) => setFormatValue(String(e.target.value))}
          options={layouts
            .find((item) => item.id === layoutId)
            ?.formats.map((item) => ({
              label: item.displayName,
              value: item.id,
            }))}
          value={formatValue}
        />
        <SelectInput
          label={t('print_scale')}
          name="Scale"
          onChange={(e: {
            target: {
              id: string;
              name: string;
              value: string | number | (string | number)[];
            };
          }) => setPrintScale(Number.parseInt(String(e.target.value), 10))}
          options={scaleValues.map((item) => ({
            label: `1:${item}`,
            value: item,
          }))}
          value={printScale}
        />
        <Input
          label={t('print_angle')}
          name="Angle"
          title={t('print_angle_info')}
          onFocus={handleAngleFocus}
          onBlur={handleAngleBlur}
          onChange={handleAngleChanged}
          initialValue={inputAngle}
          value={inputAngle}
        />
        <ToggleInputGroup
          horizontal
          type="radio"
          name="LayoutOrientation"
          label={t('print_layout_orientation')}
          value={[orientation]}
          onInputChange={(value) => setOrientation(String(value))}
          controlled
        >
          {format?.landscape && (
            <ToggleInput label={t('print_orientation_landscape')} value="landscape" />
          )}
          {format?.portrait && (
            <ToggleInput label={t('print_orientation_portrait')} value="portrait" />
          )}
        </ToggleInputGroup>
        <ToggleInputGroup
          horizontal
          type="radio"
          name="LegendVisible"
          label={t('print_export_format')}
          value={[String(printMapOnly)]}
          onInputChange={togglePrintMapOnly}
        >
          <ToggleInput label="PDF" value="false" />
          <ToggleInput label="PNG" value="true" infoText={t('print_export_png')} />
        </ToggleInputGroup>
        <div className="form__entry">
          <div className="form__label">
            <label htmlFor="LegendVisible">{t('print_legend')}</label>
          </div>
          <div className="form__field">
            <Switch
              label=""
              id="LegendVisible"
              onChange={togglePrintLegend}
              checked={printLegend}
              additionalClassNames="field__switch"
              disabled={printMapOnly}
            />
          </div>
        </div>
        {selectedTopicLayers.map((id) => (
          <PrintVisibleLayers
            key={id}
            ref={(ref) => {
              visibleLayersRefs.current[id] = ref;
            }}
            url={topicLayers[id].url}
            topicId={id}
            isPublic={topicLayers[id].isPublic ?? true}
            printScale={printScale}
          />
        ))}
        <ButtonAV
          onClick={handleResetPrint}
          additionalClassNames="button__as-link button__max-width"
        >
          {t('form_reset')}
        </ButtonAV>
        <div title={printButtonTitle}>
          <Button
            additionalClassNames="button__max-width"
            onClick={onSubmit}
            disabled={printButtonDisabled}
          >
            {t('print_print')}
          </Button>
        </div>
      </Scrollable>
      <PrintedList />
    </div>
  );
};

export default Print;
